/**
 * FieldReauthPrompt (Inventory Screens Step 5.2c Part A) — the dead-session
 * interstitial for the field zone.
 *
 * Rendered by FieldProtectedRoute when there is NO live MSAL account but a
 * valid tmg-field snapshot exists AND the device is online (navigator.onLine).
 * That combination is a silently-died session, not a field outage: a
 * recreated tab drops the sessionStorage MSAL cache while the IDB snapshot
 * survives (audit_field_auth_degradation_20260713.md). Rendering the zone
 * would fake an offline shell on a live network — null callerEmail →
 * refdata air-gap (empty dropdowns) + stranded null-caller queue items.
 *
 * One action only: loginRedirect, the same mechanism LoginPage.jsx uses.
 * Deliberately NO back/dismiss — every other path out of this state renders
 * a degraded zone; the only correct exits are sign-in or going genuinely
 * offline. Queued work is safe: the outbox lives in IndexedDB and drains on
 * the zone's normal cycle once a callerEmail exists again (useFieldDrain).
 */

import React, { useState } from 'react';
import { msalInstance } from '../../../auth/msalInstance';
import { loginRequest } from '../../../authConfig';
import './FieldReauthPrompt.css';

export default function FieldReauthPrompt() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSignIn = () => {
    setLoading(true);
    setError(null);
    msalInstance.loginRedirect(loginRequest).catch((e) => {
      console.error('FieldReauthPrompt: login error:', e);
      setError('Sign in failed. Please try again.');
      setLoading(false);
    });
  };

  return (
    <div className="frp-page" data-testid="field-reauth">
      <div className="frp-card">
        <h1 className="frp-headline">Session expired</h1>
        <p className="frp-body">
          Captures saved on this device are safe and will sync after you
          sign back in.
        </p>
        <button
          type="button"
          className="frp-signin-btn"
          data-testid="field-reauth-btn"
          onClick={handleSignIn}
          disabled={loading}
        >
          {loading ? 'Redirecting…' : 'Reconnect & sign in'}
        </button>
        {error && (
          <p className="frp-error" data-testid="field-reauth-error">{error}</p>
        )}
      </div>
    </div>
  );
}
